import React, { useEffect, useState } from "react";
import { api } from "../api/client";
import { isCapacitorIOS, getCapacitorPlugin } from "../lib/capacitorEnv";

/**
 * AppleButton — Sign in with Apple entry point.
 *
 * Rendered only when ALL of the following are true:
 *   1. The app runs inside the Capacitor iOS shell.
 *   2. The ``SignInWithApple`` Capacitor plugin is registered
 *      (``window.Capacitor.Plugins.SignInWithApple.authorize`` exists).
 *   3. The backend reports Apple sign-in as available via
 *      ``GET /api/auth/apple/available`` (which is only true when the
 *      ``APPLE_CLIENT_ID`` env var is configured in production).
 *
 * If any check fails the component renders NOTHING — we never present a
 * button that looks tappable but silently 503-s mid-flow.
 */
export function useIsAppleSignInAvailable() {
  const [available, setAvailable] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!isCapacitorIOS()) return;
    const plugin = getCapacitorPlugin("SignInWithApple");
    if (!plugin || typeof plugin.authorize !== "function") return;
    api.get("/auth/apple/available")
      .then((r) => {
        if (!cancelled && r?.data?.available) setAvailable(true);
      })
      .catch(() => { /* availability probe failed — button stays hidden */ });
    return () => { cancelled = true; };
  }, []);
  return available;
}

export function AppleButton({ onClick, testID }) {
  const available = useIsAppleSignInAvailable();
  if (!available) return null;

  return (
    <button
      onClick={onClick}
      data-testid={testID}
      style={{
        width: "100%",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        padding: "12px 22px",
        borderRadius: 999,
        background: "#000",
        color: "#fff",
        border: "1px solid #000",
        fontSize: 15,
        fontWeight: 600,
        cursor: "pointer",
        transition: "background 150ms",
        marginBottom: 12,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = "#1a1a1a"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "#000"; }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path
          fill="#fff"
          d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
        />
      </svg>
      Continuer avec Apple
    </button>
  );
}

export default AppleButton;
